import { http } from '@/api/http';
import { routeApiFetcher } from '@/api/apis/route';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import {
  RouteResponse,
  RouteSummaryType,
  HereGeocodeItemType,
  CalculateGroundRouteParams,
  HereGeocodeResultType,
} from './here-maps.types';
import { getHereMapsApiUrl } from './here-maps.paths';
import { DEFAULT_COUNTRY_ISO3 } from '@/constants/location.constants';

const setHereMapsToken = async () => {
  const data = await routeApiFetcher.createHereMapsToken();

  localStorage.setItem('hereMapsToken', data.accessToken);
  localStorage.setItem('hereMapsTokenExpiresAt', `${Date.now() + data.expiresIn * 1000}`);

  return data.accessToken;
};

export const checkHereMapsToken = async (): Promise<string> => {
  const token = localStorage.getItem('hereMapsToken');
  const tokenExpiresAt = localStorage.getItem('hereMapsTokenExpiresAt');

  if (!token || !tokenExpiresAt) {
    return setHereMapsToken();
  }

  if (Number(tokenExpiresAt) - Date.now() < 5 * MINUTE_IN_MS) {
    return setHereMapsToken();
  }

  return token;
};

const getHereMapsAuthHeaders = async () => ({
  Authorization: `Bearer ${await checkHereMapsToken()}`,
});

export const calculateGroundRoute = async ({
  startLocation,
  endLocation,
  type,
  timestamp,
}: CalculateGroundRouteParams): Promise<{ polylines: string[]; summary: RouteSummaryType; isFeasible: boolean }> => {
  const headers = await getHereMapsAuthHeaders();

  const response = await http.get<RouteResponse>(getHereMapsApiUrl.calculateRoutes(), {
    headers,
    params: {
      transportMode: 'car',
      origin: `${startLocation.lat},${startLocation.lng}`,
      destination: `${endLocation.lat},${endLocation.lng}`,
      avoid: {
        features: 'ferry',
      },
      return: 'polyline,actions,summary',
      ...(type && timestamp ? { [type]: timestamp } : {}),
    },
  });

  const data = response.data.routes.map((route) => route.sections).flat();
  const hasFerrySections = data.some((s) => s.transport.mode === 'ferry');

  if (hasFerrySections || !response.data.routes.length) {
    return { polylines: [], summary: { duration: 0, length: 0 }, isFeasible: false };
  }

  const polylines = response.data.routes.map((route) => route.sections.map((s) => s.polyline)).flat();

  const summary = response.data.routes
    .map((route) => route.sections.map((s) => s.summary))
    .flat()
    .reduce<{ duration: number; length: number }>(
      (acc, item) => {
        return {
          ...acc,
          duration: acc.duration + item.duration,
          length: acc.length + item.length,
        };
      },
      { duration: 0, length: 0 },
    );

  return { polylines, summary, isFeasible: true };
};

type GetAddressAutosuggestParams = {
  query: string;
  country?: string;
  centerPoints?: number[];
  bbox?: number[];
};

export const getAddressAutosuggest = async ({
  query,
  country = DEFAULT_COUNTRY_ISO3,
  centerPoints,
  bbox,
}: GetAddressAutosuggestParams): Promise<{ items: HereGeocodeItemType[] }> => {
  const headers = await getHereMapsAuthHeaders();

  const countryCodeSearch = `countryCode:${country}`;
  const bboxSearch = bbox ? `bbox:${bbox.join(',')}` : '';

  const response = await http.get<{ items: HereGeocodeItemType[] }>(getHereMapsApiUrl.addressAutosuggest(), {
    params: {
      q: query,
      ...(centerPoints && !bboxSearch
        ? {
            at: centerPoints.join(','),
          }
        : {}),
      in: [countryCodeSearch, bboxSearch].filter(Boolean),
      lang: 'en',
      show: 'details,tz',
    },
    paramsSerializer: {
      indexes: null,
    },
    headers,
  });

  const items = response.data?.items ?? [];
  const validItems = items.filter((item) => {
    return ['houseNumber', 'postalCode', 'place'].includes(item.resultType);
  });

  if (!validItems.length) {
    return { items: [] };
  }

  return { items: validItems };
};

export const getAddressByReverseGeocode = async (coords: number[]): Promise<HereGeocodeItemType | null> => {
  const headers = await getHereMapsAuthHeaders();

  const response = await http.get<{ items: HereGeocodeItemType[] }>(getHereMapsApiUrl.reverseGeocode(), {
    params: {
      at: coords.join(','),
      types: 'address,place',
      show: 'tz',
    },
    headers,
  });

  const items = response.data?.items ?? [];
  const validItems = items.filter((item) => {
    return ['houseNumber', 'postalCode', 'place'].includes(item.resultType);
  });

  if (!validItems.length) {
    return null;
  }

  return validItems[0];
};

export const getAddressByHereMapsId = async (id: string): Promise<HereGeocodeItemType> => {
  const headers = await getHereMapsAuthHeaders();

  const response = await http.get<HereGeocodeItemType>(getHereMapsApiUrl.lookup(), {
    params: {
      id,
      show: 'tz',
    },
    headers,
  });

  return response.data;
};

type FetchGeocodeParams = {
  country: string;
  state?: string;
};

const fetchGeocodeByQuery = async (
  q: string,
  country: string,
  headers: Record<string, string>,
): Promise<HereGeocodeItemType | null> => {
  const response = await http.get<{ items: HereGeocodeItemType[] }>(getHereMapsApiUrl.geocode(), {
    params: {
      q,
      in: `countryCode:${country}`,
      types: 'area',
      limit: 5,
    },
    headers,
  });

  const items = response.data?.items ?? [];
  const administrativeItems = items.filter((item) => item.resultType === HereGeocodeResultType.AdministrativeArea);
  return administrativeItems[0] ?? items[0] ?? null;
};

export const fetchGeocode = async ({ country, state }: FetchGeocodeParams): Promise<HereGeocodeItemType | null> => {
  try {
    const headers = await getHereMapsAuthHeaders();

    if (state) {
      const stateResult = await fetchGeocodeByQuery(state, country, headers);
      if (stateResult) return stateResult;
    }

    return await fetchGeocodeByQuery(country, country, headers);
  } catch (e) {
    // eslint-disable-next-line
    console.error(e);
    return null;
  }
};

export const hereMapsFetcher = {
  getAddressAutosuggest,
  calculateGroundRoute,
  getAddressByReverseGeocode,
};
